/**
 * NotificationBell — header bell with unread badge + dropdown (lane A2, 2026-10-04).
 *
 * Backed by social-api `/api/notifications*` (services/notifications.py): failed
 * posts, approvals, approval decisions, expiring sign-ins, comment digests,
 * finished renders. The badge polls every 60 s (paused while the tab is hidden);
 * the list is fetched only while the dropdown is open.
 *
 * Why not `@plerity/notifications`: that package is the desktop/OS-notification
 * provider + toast layer (permission flow, focus gate) — it has no REST list
 * client, and ops' bell is app-local too. This component is the social-local
 * equivalent against social-api.
 *
 * Mobile (375 px): the panel is pinned to the viewport (`fixed inset-x-3`), so
 * it can never overflow the left edge; from `sm` it anchors under the bell.
 */

import { EmptyState } from "@plerity/ui-core";
import {
  AlertTriangle,
  Bell,
  CheckCheck,
  CheckCircle2,
  ClipboardCheck,
  Film,
  KeyRound,
  LibraryBig,
  Link2,
  type LucideIcon,
  MailWarning,
  MessageCircle,
  MessagesSquare,
  Radar,
  Smartphone,
  Star,
} from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  type AppNotification,
  NOTIFICATION_KIND_LABELS,
  type NotificationKind,
  useMarkNotificationsRead,
  useNotificationList,
  useNotificationUnreadCount,
} from "../api/notifications";

const KIND_ICONS: Record<NotificationKind, { icon: LucideIcon; tone: string }> = {
  publish_failed: { icon: AlertTriangle, tone: "text-red-600 dark:text-red-400" },
  post_published: { icon: CheckCircle2, tone: "text-green-600 dark:text-green-400" },
  approval_requested: { icon: ClipboardCheck, tone: "text-amber-600 dark:text-amber-400" },
  approval_decision: { icon: MessagesSquare, tone: "text-brand dark:text-brand" },
  token_expiring: { icon: KeyRound, tone: "text-amber-600 dark:text-amber-400" },
  comments_digest: { icon: MessageCircle, tone: "text-brand dark:text-brand" },
  reviews_digest: { icon: Star, tone: "text-amber-600 dark:text-amber-400" },
  approval_stale: { icon: ClipboardCheck, tone: "text-amber-600 dark:text-amber-400" },
  render_finished: { icon: Film, tone: "text-purple-600 dark:text-purple-400" },
  listening_alert: { icon: Radar, tone: "text-teal-600 dark:text-teal-400" },
  account_connected: { icon: Link2, tone: "text-green-600 dark:text-green-400" },
  report_delivery_failed: { icon: MailWarning, tone: "text-red-600 dark:text-red-400" },
  dm_attention: { icon: MessageCircle, tone: "text-amber-600 dark:text-amber-400" },
  video_autopilot_blocked: { icon: AlertTriangle, tone: "text-amber-600 dark:text-amber-400" },
  video_drafts_ready: { icon: Film, tone: "text-purple-600 dark:text-purple-400" },
  content_library_pending: { icon: LibraryBig, tone: "text-brand dark:text-brand" },
  content_library_digest: { icon: LibraryBig, tone: "text-brand dark:text-brand" },
  story_manual_due: { icon: Smartphone, tone: "text-fuchsia-700 dark:text-fuchsia-300" },
};

export function formatTimeAgo(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.max(0, Math.floor((now - then) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** Only same-app paths are navigable (the API already enforces this). */
function safeHref(href: string | null): string | null {
  return href?.startsWith("/") && !href.startsWith("//") ? href : null;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const navigate = useNavigate();

  const unreadQuery = useNotificationUnreadCount();
  const listQuery = useNotificationList(open);
  const markRead = useMarkNotificationsRead();

  const unread = open && listQuery.data ? listQuery.data.unread_count : (unreadQuery.data?.unread ?? 0);
  const items = listQuery.data?.items ?? [];

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  }, []);

  // Click outside / Escape close the panel.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) close(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close(true);
      }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const openItem = (n: AppNotification) => {
    if (!n.read_at) markRead.mutate({ ids: [n.id] });
    const href = safeHref(n.href);
    close(false);
    if (href) navigate(href);
  };

  const badge = unread > 99 ? "99+" : String(unread);

  return (
    <div ref={wrapperRef} className="relative" data-help-context="social-notification-bell">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
      >
        <Bell className="h-5 w-5" aria-hidden="true" />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-3xs font-bold leading-none text-white dark:bg-red-600 dark:text-white"
            aria-hidden="true"
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <section
          id={panelId}
          aria-label="Notifications"
          className="fixed inset-x-3 top-16 z-50 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96"
        >
          <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</h2>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markRead.mutate({ all: true })}
                disabled={markRead.isPending}
                className="inline-flex items-center gap-1 rounded text-xs font-medium text-brand-hover hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus disabled:opacity-50 dark:text-brand"
              >
                <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
                Mark all read
              </button>
            )}
          </div>

          {markRead.isError && (
            <p
              role="alert"
              className="border-b border-gray-200 px-4 py-2 text-xs text-red-600 dark:border-gray-700 dark:text-red-400"
            >
              Couldn't update your notifications. Please try again.
            </p>
          )}

          <div className="max-h-[min(28rem,70vh)] overflow-y-auto">
            {listQuery.isError ? (
              <EmptyState
                size="sm"
                icon={<AlertTriangle className="h-8 w-8 text-red-500 dark:text-red-400" aria-hidden="true" />}
                title="Couldn't load notifications"
                description="Something went wrong fetching your alerts."
                actionLabel="Retry"
                onAction={() => listQuery.refetch()}
              />
            ) : listQuery.isLoading ? (
              // biome-ignore lint/a11y/useSemanticElements: role=status is the WAI-ARIA live-region pattern for a loading placeholder.
              <div role="status" aria-label="Loading notifications" className="space-y-3 p-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-1.5">
                    <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                    <div className="h-3 w-1/2 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                  </div>
                ))}
              </div>
            ) : items.length === 0 ? (
              <EmptyState
                size="sm"
                icon={<Bell className="h-8 w-8 text-gray-400 dark:text-gray-500" aria-hidden="true" />}
                title="You're all caught up"
                description="Failed posts, approvals, expiring sign-ins and new comments will show up here."
              />
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {items.map((n) => (
                  <NotificationRow
                    key={n.id}
                    notification={n}
                    onOpen={() => openItem(n)}
                    onMarkRead={() => markRead.mutate({ ids: [n.id] })}
                  />
                ))}
              </ul>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function NotificationRow({
  notification: n,
  onOpen,
  onMarkRead,
}: {
  notification: AppNotification;
  onOpen: () => void;
  onMarkRead: () => void;
}) {
  const unread = !n.read_at;
  const meta = KIND_ICONS[n.kind] ?? KIND_ICONS.comments_digest;
  const Icon = meta.icon;
  return (
    <li
      className={`flex items-start gap-1 ${unread ? "bg-info-50 dark:bg-info-900/30" : "bg-white dark:bg-gray-800"}`}
      data-unread={unread ? "true" : "false"}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-focus dark:hover:bg-gray-700"
      >
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${meta.tone}`} aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            {unread && <span className="sr-only">Unread: </span>}
            {n.title}
          </span>
          {n.body && (
            <span className="mt-0.5 line-clamp-2 block text-xs text-gray-600 dark:text-gray-300">{n.body}</span>
          )}
          <span className="mt-1 flex items-center gap-2 text-3xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <span>{NOTIFICATION_KIND_LABELS[n.kind] ?? n.kind}</span>
            <span aria-hidden="true">·</span>
            <time dateTime={n.created_at}>{formatTimeAgo(n.created_at)}</time>
          </span>
        </span>
      </button>
      {unread && (
        <button
          type="button"
          onClick={onMarkRead}
          aria-label={`Mark "${n.title}" as read`}
          className="mr-2 mt-3 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
        >
          <CheckCheck className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </li>
  );
}

export default NotificationBell;
